import {
  Dropdown,
  TextInput,
  Textarea,
  type IDropdownItem,
} from "@business-finland/wif-ui-lib";
import { useCallback, useId } from "react";

import { LocalesEmployers } from "../../../../constants/locale";
import useFormField from "../../../../hooks/useFormField";
import type { WifService } from "../../../../types/wif-companies";
import { companyFormDefaultText, companyWebsiteRegex } from "../constants";
import {
  getCompanyFormClsx,
  getCompanyFormStyleClassName,
  useFormFieldChecks,
} from "../helpers";
import {
  useCompanyFormDispatch,
  companyFormActions,
  useCompanyFormState,
} from "../store";
import type {
  CompanyFormFieldProps,
  CompanyDescriptionFormFieldProps,
} from "../types";

export interface ICompanyServicesProps extends Partial<CompanyFormFieldProps> {
  services: WifService[];
  heading: string;
  companyWebsiteField: CompanyFormFieldProps | undefined;
  companyDescriptionField: CompanyFormFieldProps | undefined;
}

export function TypeOfServices({
  services,
  labelText = companyFormDefaultText.typeOfServiceLabel,
  placeholderText = companyFormDefaultText.typeOfServicePlaceholder,
}: {
  services: WifService[];
  labelText?: string;
  placeholderText?: string;
}) {
  const id = "company__type-of-services";
  const { companyServices } = useCompanyFormState();
  const dispatch = useCompanyFormDispatch();

  const handleItemSelect = useCallback(
    (items: IDropdownItem[]) => {
      dispatch(companyFormActions.setValue({ companyServices: items }));
    },
    [dispatch]
  );

  return (
    <div className={getCompanyFormClsx("form_field")}>
      <label htmlFor={id} className="texts__caption">
        {labelText}*
      </label>

      <Dropdown
        id={id}
        name={"companyServices"}
        label={placeholderText}
        items={services}
        size="large"
        enableCloseOnSelect={false}
        selectedItems={companyServices}
        onSelectedItemsChange={handleItemSelect}
      />
    </div>
  );
}

export function LocalDescription({
  companyDescriptionField,
  companyWebsiteField,
  locale,
}: {
  companyDescriptionField: CompanyDescriptionFormFieldProps | undefined;
  companyWebsiteField: CompanyFormFieldProps | undefined;
  locale: LocalesEmployers;
}): JSX.Element {
  return (
    <div className={getCompanyFormStyleClassName("services__set")}>
      <CompanyDescription locale={locale} {...companyDescriptionField} />
      <CompanyWebsite locale={locale} {...companyWebsiteField} />
    </div>
  );
}

function CompanyDescription({
  labelText = companyFormDefaultText.companyDescriptionLabel,
  placeholderText = companyFormDefaultText.companyDescriptionPlaceholder,
  locale,
  maxLength = "500",
}: Partial<CompanyDescriptionFormFieldProps> & { locale: LocalesEmployers }) {
  const state = useCompanyFormState();
  const dispatch = useCompanyFormDispatch();

  const name = "companyDescription";
  const id = `${useId()}__${name}-${locale}`;
  const value = state.companyDescription?.[locale]?.toString() || "";

  const handleChange = useCallback(
    (value: string) =>
      dispatch(companyFormActions.setDescription({ [locale]: value })),
    [dispatch, locale]
  );

  return (
    <div className={getCompanyFormClsx("form_field")}>
      <Textarea
        label={`${labelText} (${locale.toUpperCase()})`}
        id={id}
        maxLength={parseInt(maxLength, 10)}
        name={`${name}-${locale}`}
        value={value}
        onValueChange={handleChange}
        placeholder={placeholderText}
      />
    </div>
  );
}

function CompanyWebsite({
  labelText = companyFormDefaultText.companyWebsiteLabel,
  placeholderText = companyFormDefaultText.companyWebsitePlaceholder,
  locale,
}: Partial<CompanyFormFieldProps> & { locale: LocalesEmployers }) {
  const name = "companyWebsite";

  const dispatch = useCompanyFormDispatch();
  const checks = useFormFieldChecks(false, companyWebsiteRegex);

  const onError = useCallback(
    () => dispatch(companyFormActions.setWebsite({ [locale]: "" })),
    [dispatch, locale]
  );
  const onSuccess = useCallback(
    (value: string) =>
      dispatch(companyFormActions.setWebsite({ [locale]: value })),
    [dispatch, locale]
  );

  const { inputProps } = useFormField({
    name: `${name}-${locale}`,
    onError,
    onSuccess,
    checks,
  });

  return (
    <TextInput
      label={labelText + ` (${locale.toUpperCase()})`}
      required={false}
      placeholder={placeholderText}
      autoComplete="off"
      {...inputProps}
    />
  );
}
